<script setup>
import { ref, computed, watch } from 'vue';
import * as calc from '../calc.js';
import { useShared } from '../state.js';
import { usePersisted } from '../use_persisted.js';
import { scopedKey, useSetups } from '../setups.js';
import { useToasts } from '../use_toasts.js';
import RecomputeButton from './recompute_button.vue';
import UnitSelect from './unit_select.vue';
import CutAngleCamera from './cut_angle_camera.vue';
import { supportsCutAngleCamera } from '../device_capabilities.js';

const { inputs, computedResult, isStale, recompute } = useShared();
const { pushToast } = useToasts();
const { activeSetupId } = useSetups();

// Capabilities don't change mid-session, so this is checked once rather
// than kept reactive — see device_capabilities.js for why it's a heuristic
// (no camera/motion permission is requested just to decide this).
const showCutCameraButton = supportsCutAngleCamera();

// Tracks the last error message we toasted, so retyping through the same
// broken value on every keystroke doesn't spam a fresh popup each time.
let lastMarkError = null;

// Scoped to the active setup (evaluated once at mount) rather than global —
// app.vue remounts this component whenever the active setup changes, so
// each setup keeps its own last-used distance/unit/cut here.
const input = usePersisted(scopedKey('mark-inputs', activeSetupId.value), {
  distance: 70,
  unit: 'm',
  cut: 0,
  sight_bar: 1,
});


// ── Local "has been computed" flag ──
// False until the user presses the button for the first time
const hasComputed = ref(false);

// Governs the Effective Distance row's visibility — with no cut, the
// effective distance is always just the input distance itself (see
// findCutDistance's own cut_angle-0 shortcut in calc.js), so showing it
// adds nothing beyond what Distance already says.
const hasCutAngle = computed(() => {
  const cutDegrees = input.value.cut === "" ? 0 : Number(input.value.cut);
  return cutDegrees !== 0;
});

const EMPTY_LOCAL_COMPUTED = {
  mark: '',
  cutDistance: '',
  velocity: '',
  time: '',
  drop: '',
  vertical: '',
  horizontal: '',
};

// A ref + explicit updateLocalComputed() rather than a computed(): staying
// async (even though every calc.js call here is actually synchronous on
// this branch — findCutDistance resolves entirely in Rust, no JS-side
// table to await) keeps this component's shape the same as the plain-JS
// branch, where the equivalent lookup genuinely can be async. The reactive
// dependencies a computed() would have tracked (input, isStale,
// computedResult, inputs.sight_settings) are watched explicitly instead,
// below.
const localComputed = ref({ ...EMPTY_LOCAL_COMPUTED });

async function updateLocalComputed() {
  let vars = input.value;

  console.log("compute mark: " + JSON.stringify(vars));

  if (isStale.value) {
    console.log("full computation not done, can't calculate");
    localComputed.value = { ...EMPTY_LOCAL_COMPUTED };
    return;
  }

  if (vars.distance === "" || vars.distance == 0) {
    console.log("distance empty, can't calculate");
    localComputed.value = { ...EMPTY_LOCAL_COMPUTED };
    return;
  }

  if (vars.sight_bar === "") {
    console.log("sight bar empty, can't calculate");
    localComputed.value = { ...EMPTY_LOCAL_COMPUTED };
    return;
  }

  // An empty cut is a flat (no-cut) shot, not a missing/invalid value —
  // the common case for most marks — so it defaults to 0 rather than
  // blocking the computation the way an empty distance/sight bar does.
  const cutDegrees = vars.cut === "" ? 0 : vars.cut;

  if (cutDegrees < -89 || cutDegrees > 89) {
    // Past ~±89° the shot is effectively vertical — the physics solver
    // divides by horizontal velocity, which heads to zero right around
    // there. Reject up front instead of surfacing whatever cryptic
    // failure falls out of that division.
    if (lastMarkError !== 'cut-range') {
      lastMarkError = 'cut-range';
      pushToast('Compute Mark: Cut angle must be between -89° and 89°');
    }
    localComputed.value = { ...EMPTY_LOCAL_COMPUTED };
    return;
  }

  try {
    let d = vars.distance * calc.toScale(vars.unit);
    // This app's Cut convention is the opposite of calc.js's cut_angle: a
    // positive Cut value here means the target is BELOW the shooter
    // (downhill), negative means above (uphill) — see
    // cut_angle_camera.vue's own comment on this, since that's where a
    // measured angle is produced already in this same convention.
    // Negating here converts it to calc.js's convention instead (positive
    // cut_angle = target above/uphill, per cutDists' y_target = sin(cut_angle) * d).
    let cut = cutDegrees * -calc.radian;

    let computed = computedResult.value;

    let { mark: m, angle: a, fps, t, drop, verticalMarks, horizontalMarks } = calc.calculateMark(
        inputs.sight_settings, vars.sight_bar,
        computed,
        cut, d);

    lastMarkError = null;

    // The level-ground distance that calls for this same mark, shared with
    // cuts_table.vue (see calc.findCutDistance / rust/calc-core's
    // find_cut_distance_in_table) — runs entirely in Rust, reusing the
    // same cached level-ground table cuts_table.vue's calc.cutsGrid does,
    // so no table needs building or passing in from here. Separate
    // try/catch from the mark computation above: an extreme cut angle can
    // leave no level-ground distance achieving the same mark at all
    // (findCutDistance returns null), which shouldn't blank out the mark
    // itself, just this one extra figure.
    let cutDistance = '';
    try {
      const cutDistanceM = calc.findCutDistance(inputs.sight_settings, vars.sight_bar, computed, cut, d);
      if (cutDistanceM !== null) {
        cutDistance = (cutDistanceM / calc.toScale(vars.unit)).toFixed(2) + " " + vars.unit;
      }
    } catch (error) {
      console.log('error computing cut distance:', error.message);
    }

    localComputed.value = {
      mark: calc.roundToClick(inputs.sight_settings, m),
      cutDistance,
      velocity: fps.toFixed(1) + " fps",
      time: t.toFixed(3) + " s",
      drop: drop.toFixed(1) + " cm/m",
      vertical: verticalMarks.toFixed(2) + " cm/turn",
      horizontal: horizontalMarks.toFixed(2) + " cm/turn",
    };

  } catch (error) {
    console.log('error computing mark:', error.message);
    console.log(error.stack);
    if (error.message !== lastMarkError) {
      lastMarkError = error.message;
      pushToast(`Compute Mark: ${error.message}`);
    }
    localComputed.value = { ...EMPTY_LOCAL_COMPUTED };
  }
}

// Replicates what computed()'s automatic dependency tracking used to do —
// re-run whenever any input this depends on changes, including
// computedResult/sight_settings changing from elsewhere on the page (e.g.
// the main Recompute button), not just this component's own fields.
watch([input, isStale, computedResult, () => inputs.sight_settings], () => {
  updateLocalComputed();
}, { deep: true, immediate: true });


// ── Local dirty flag: local inputs changed since last compute ──
const localDirty = ref(true);

function markLocalDirty() {
  localDirty.value = true;
}

// ── Camera-based cut angle finder ──
const showCutCamera = ref(false);

function onCutAngleFromCamera(angle) {
  input.value.cut = angle;
  markLocalDirty();
  runComputation();
}

// Flat/no-cut is the common case (see localComputed's cutDegrees default),
// but only worth a dedicated shortcut next to the camera reading button —
// on a desktop, clearing the field by hand is no real hassle, but on a
// phone (where that button shows at all, see supportsCutAngleCamera) it's
// one more on-screen-keyboard round trip than this button.
function zeroCutAngle() {
  input.value.cut = 0;
  markLocalDirty();
  runComputation();
}

async function runComputation() {
  console.log("runComputation");

  if (isStale.value) {
    console.log("recompute everything");
    await recompute();
  }

  console.log("trigger recompute");

  // Step 2: force local re-evaluation
  await updateLocalComputed();

  console.log("clear flags");
  // Step 3: clear flags
  localDirty.value = false;
  hasComputed.value = true;
}

</script>

<template>

  <div class="centered">
    <RecomputeButton />
  </div>

  <div class="flex">
    <table>
        <tr>
          <td>Distance</td>
          <td class="right-align">
            <input type="number" class="input-measure field"
              @input="markLocalDirty"
              @keyup.enter="runComputation"
              v-model.number="input.distance" />
          </td>
        </tr>
        <tr>
          <td>Unit</td>
          <td class="right-align">
            <UnitSelect v-model="input.unit" full-width @update:modelValue="runComputation" />
          </td>
        </tr>
        <tr>
          <td>Cut</td>
          <td class="right-align">
            <button v-if="showCutCameraButton" type="button" class="btn-cut-action" title="Measure cut angle with camera"
              @click="showCutCamera = true">
              <svg viewBox="0 0 24 24" width="1.1em" height="1.1em" aria-hidden="true">
                <circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" stroke-width="1.5" />
                <line x1="12" y1="0.5" x2="12" y2="6" stroke="currentColor" stroke-width="1.5" />
                <line x1="12" y1="18" x2="12" y2="23.5" stroke="currentColor" stroke-width="1.5" />
                <line x1="0.5" y1="12" x2="6" y2="12" stroke="currentColor" stroke-width="1.5" />
                <line x1="18" y1="12" x2="23.5" y2="12" stroke="currentColor" stroke-width="1.5" />
              </svg>
            </button>
            <button v-if="showCutCameraButton" type="button" class="btn-cut-action" title="Set cut angle to 0°"
              @click="zeroCutAngle">
              0°
            </button>
            <input type="number" class="input-measure field"
              @input="markLocalDirty"
              @keyup.enter="runComputation"
              v-model.number="input.cut" />
          </td>
        </tr>
        <tr>
          <td>Sight Bar</td>
          <td class="right-align">
            <input type="number" class="input-measure field"
              @input="markLocalDirty"
              @keyup.enter="runComputation"
              v-model.number="input.sight_bar" />
          </td>
        </tr>
    </table>

    <table>
     <tr>
        <td>Mark:</td>
        <td class="right-align">
          <input class="input-result field" type="text" readonly
            v-model.string="localComputed.mark" />
        </td>
      </tr>
      <tr v-if="hasCutAngle">
        <td>Effective Distance:</td>
        <td class="right-align">
          <input class="input-result field" type="text" readonly
            v-model.string="localComputed.cutDistance" />
        </td>
      </tr>
      <tr>
        <td>Velocity at impact:</td>
        <td class="right-align">
          <input class="input-result field" type="text" readonly
            v-model.string="localComputed.velocity" />
        </td>
      </tr>
      <tr>
        <td>Time to target:</td>
        <td class="right-align">
          <input class="input-result field" type="text" readonly
            v-model.string="localComputed.time" />
        </td>
      </tr>
      <tr>
        <td>Drop:</td>
        <td class="right-align">
          <input class="input-result field" type="text" readonly
            v-model.string="localComputed.drop" />
        </td>
      </tr>
      <tr>
        <td>Vertical:</td>
        <td class="right-align">
          <input class="input-result field" type="text" readonly
            v-model.string="localComputed.vertical" />
        </td>
      </tr>
      <tr>
        <td>Horizontal:</td>
        <td class="right-align">
          <input class="input-result field" type="text" readonly
            v-model.string="localComputed.horizontal" />
        </td>
      </tr>
    </table>
  </div>

  <CutAngleCamera v-model="showCutCamera" @confirm="onCutAngleFromCamera" />
</template>

<style scoped>
.flex {
  display: flex;
  flex-wrap: wrap;          /* allow children to wrap to next line */
  justify-content: center;  /* center items horizontally on each row */
  align-content: center;    /* center rows vertically when wrapped */
  align-items: center;      /* center items within each row */
  gap: var(--space-xl);     /* padding between the two tables */
}

/* Every select/input in this component shares one width, wide enough for
   the longest values we render (e.g. "10.42 cm/turn"). */
.field {
  width: 10em;
}

.btn-cut-action {
  background: none;
  border: var(--border);
  border-radius: var(--border-radius);
  width: 2em;
  height: 2em;
  padding: 0;
  margin-right: var(--space-xs);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text);
  cursor: pointer;
  flex-shrink: 0;
  font-size: 0.85em;
}

.btn-cut-action:hover {
  background: var(--color-bg-alt);
}

</style>
